import React from 'react'
import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/24/outline'
import ExportProgressModal from './ExportProgressModal'
import type { BoldPartialLinkItem } from '../../services/payments/boldPartialLinksAfterOrderSave'

export type BoldModalPhase = 'progress' | 'result' | 'error'

interface BoldCreateOrderModalProps {
  isOpen: boolean
  phase: BoldModalPhase
  progress: {
    current: number
    total: number
    percentage: number
    currentPage: number
    totalPages: number
  }
  message: string
  paymentUrl: string | null
  partialLinks: BoldPartialLinkItem[]
  partialError: string | null
  errorMessage: string | null
  isRetrying: boolean
  isCancelling: boolean
  onAccept: () => void
  /** Elimina pedido y cierra modal (sin refrescar). */
  onRetry: () => void
  /** Elimina pedido y refresca la página (botón X). */
  onDismiss: () => void
  onCopyLink: (url: string) => void
}

function BoldCreateOrderModal({
  isOpen,
  phase,
  progress,
  message,
  paymentUrl,
  partialLinks,
  partialError,
  errorMessage,
  isRetrying,
  isCancelling,
  onAccept,
  onRetry,
  onDismiss,
  onCopyLink
}: BoldCreateOrderModalProps) {
  if (!isOpen) return null

  if (phase === 'progress') {
    return (
      <ExportProgressModal
        isOpen={true}
        progress={progress}
        message={message}
        title="Procesando pago Bold"
      />
    )
  }

  if (phase === 'error') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
        <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-lg w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-red-700">Link de pago no disponible</h2>
            <button
              type="button"
              onClick={onDismiss}
              disabled={isCancelling || isRetrying}
              className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            No se pudo completar la generación del enlace de pago Bold. Revisa el detalle a continuación e
            intenta de nuevo desde el formulario.
          </p>
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            {errorMessage || 'No se pudo generar el enlace de pago. Verifica el monto, la tienda y los datos del pedido.'}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying || isCancelling}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {isRetrying ? 'Procesando…' : 'Reintentar'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const showResult =
    paymentUrl || partialLinks.length > 0 || partialError

  if (phase !== 'result' || !showResult) return null

  const hasPartialFailure = Boolean(partialError) && partialLinks.length === 0 && !paymentUrl
  const hasPartialWarning = Boolean(partialError) && partialLinks.length > 0

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-custom-blue">
            {partialLinks.length > 0
              ? 'Links de pago Bold generados'
              : paymentUrl
                ? 'Link de pago Bold generado'
                : 'Resultado — links Bold'}
          </h2>
          <button
            type="button"
            onClick={hasPartialFailure ? onDismiss : onAccept}
            disabled={hasPartialFailure && (isCancelling || isRetrying)}
            className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {partialError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            {partialError}
          </div>
        )}
        {partialLinks.length > 0 ? (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Comparte cada enlace con el cliente según corresponda al tramo Bold:
            </p>
            <ul className="space-y-4" aria-label="Lista de links Bold por pago parcial">
              {partialLinks.map((item) => (
                <li key={item.index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">{item.label}</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm text-blue-600 hover:underline truncate"
                    >
                      {item.url}
                    </a>
                    <button
                      type="button"
                      onClick={() => onCopyLink(item.url)}
                      className="flex-shrink-0 p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Copiar link"
                      aria-label={`Copiar link ${item.label}`}
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : paymentUrl ? (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Comparte este enlace con el cliente para que realice el pago:
            </p>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm text-blue-600 hover:underline truncate"
              >
                {paymentUrl}
              </a>
              <button
                type="button"
                onClick={() => onCopyLink(paymentUrl)}
                className="flex-shrink-0 p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Copiar link"
                aria-label="Copiar link"
              >
                <DocumentDuplicateIcon className="h-5 w-5" />
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-600">
            No se generaron los enlaces de pago Bold. Usa <strong>Reintentar</strong> para volver al formulario o
            cierra esta ventana con la X.
          </p>
        )}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          {hasPartialFailure && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying || isCancelling}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-medium"
            >
              {isRetrying ? 'Procesando…' : 'Reintentar'}
            </button>
          )}
          {!hasPartialFailure && (
            <button
              type="button"
              onClick={onAccept}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium"
            >
              Aceptar
            </button>
          )}
          {hasPartialWarning && (
            <button
              type="button"
              onClick={onAccept}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Aceptar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default BoldCreateOrderModal
